<template>
  <div
    v-if="list.length"
    :ref="'v-slideshow-' + id"
    class="v-slideshow"
    :class="[wrapClass, { 'is-focus': isFocus }]"
  >
    <template v-if="showSkeleton && !isReady">
      <Skeleton
        :width="skeletonOptions.width ? skeletonOptions.width : undefined"
        :height="skeletonOptions.height ? skeletonOptions.height : undefined"
        :padding-bottom="
          skeletonOptions.paddingBottom
            ? skeletonOptions.paddingBottom
            : undefined
        "
      />
      <div class="grid-x align-center-middle padding-top-xs padding-bottom-xs">
        <Skeleton
          v-if="showIndicator"
          :width="indicatorWidth"
          height="32px"
        />
      </div>
    </template>
    <div
      v-show="!showSkeleton || isReady"
      v-touch-outside="touchOutside"
      class="v-slideshow-content"
      @mouseenter="scrollMouseenter"
      @mouseleave="scrollMouseleave($event)"
    >
      <div
        ref="v-slideshow-wrapper"
        class="v-slideshow-wrapper"
        @touchend.passive="touchInside"
      >
        <div class="v-button-wrapper prev">
          <button
            v-show="isReady"
            class="v-slideshow-button"
            aria-label="Previous"
            @focus="onFocus"
            @blur="onBlur"
            @click.prevent="move(-1)"
            @keyup.enter="move(-1)"
          >
            <Icon
              class="icon chevron-left-arrow"
              :icon="csgChevronSmallLeft"
              @click="$emit('slideshow-change-click', 'left')"
            />
          </button>
        </div>
        <div
          ref="v-scroller"
          :style="scrollerStyle()"
          class="v-scroller"
          @touchstart.passive="scrollerTouchStart"
          @touchmove="scrollerTouchMove"
          @touchend.passive="scrollerTouchEnd"
        >
          <ul
            class="grid-x v-slideshow"
            aria-label="slideshow"
            :style="{ 'flex-flow': 'row nowrap' }"
          >
            <li
              key="last"
              class="cell small-12 slideshow-item"
              :class="prevIndex === 0 ? 'active' : ''"
              :aria-hidden="true"
            >
              <slot
                name="item"
                :data="list[list.length - 1]"
                :index="list.length - 1"
              />
            </li>
            <li
              v-for="(item, index) in list"
              :key="index"
              class="cell small-12 slideshow-item"
              :class="
                currentPage === index + 1 || prevIndex === index + 1
                  ? 'active'
                  : ''
              "
              :aria-live="currentIndicator === index ? 'polite' : ''"
              :aria-atomic="currentIndicator === index ? true : false"
              role="region"
              :aria-roledescription="
                currentIndicator === index ? list[currentIndicator]?.title : ''
              "
            >
              <slot
                name="item"
                :data="item"
                :index="index"
              />
            </li>
            <li
              key="first"
              class="cell small-12 slideshow-item"
              :class="prevIndex === numberOfPages + 1 ? 'active' : ''"
              :aria-hidden="true"
            >
              <slot
                name="item"
                :data="list[0]"
                :index="0"
              />
            </li>
          </ul>
        </div>
        <div class="v-button-wrapper next">
          <button
            v-show="isReady"
            class="v-slideshow-button"
            aria-label="Next"
            @focus="onFocus"
            @blur="onBlur"
            @click.prevent="move(1)"
            @keyup.enter="move(1)"
          >
            <Icon
              class="icon chevron-right-arrow"
              :icon="csgChevronSmallRight"
              @click="$emit('slideshow-change-click', 'right')"
            />
          </button>
        </div>
      </div>
      <div
        v-if="showIndicator"
        :class="['v-dot-indicator-wrapper', {'slideshow-indicator': showIndicatorOnSlideshow}]"
      >
        <button
          v-if="autoplayMode === 'auto'"
          :aria-label="
            showControl === 'play'
              ? 'Start Automatic Slide Show'
              : 'Stop Automatic Slide Show'
          "
          class="secondary button-icon button-icon-small button-control"
          @click="showControl === 'play' ? startAutoplay() : stopAutoplay()"
        >
          <Icon
            :class="showControl === 'play' ? 'play-control' : 'pause-control'"
            :icon="showControl === 'play' ? csgVideoControlPlay : csgVideoControlPause"
          />
        </button>
        <ul class="grid-x align-center">
          <li
            v-for="index in numberOfPages"
            :id="index"
            :key="index"
            tabindex="0"
            role="button"
            :aria-label="'Moved to ' + index + numberSuffix(index) + ' slide'"
            :aria-current="currentPage === index"
            :class="{ active: index === currentPage }"
            @click.prevent="change(index)"
            @keypress.enter="change(index)"
          />
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { useDebounceFn } from '@vueuse/core';
import {
  csgChevronSmallLeft,
  csgChevronSmallRight,
  csgVideoControlPause,
  csgVideoControlPlay,
} from '@csgSvgIcons/icons';
import Skeleton from '../skeleton';
import generateRandomId from '../../utils/randomNumber';
import Icon from '../icon';

export default {
  name: 'Slideshow',
  components: {
    Skeleton,
    Icon,
  },
  directives: {
    'touch-outside': {
      beforeMount: (el, binding) => {
        el.clickOutsideEvent = (event) => {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value(event);
          }
        };
        document.addEventListener('touchend', el.clickOutsideEvent);
      },
      unmounted: (el) => {
        document.removeEventListener('touchend', el.clickOutsideEvent);
      },
    },
  },
  props: {
    list: {
      type: Array,
      required: true,
      default: () => [],
    },
    autoplayMode: {
      type: String,
      default: 'auto',
      validator: (val) => ['auto', 'manual', 'hover-2-slides'].includes(val),
    },
    backToFirst: {
      type: Boolean,
      default: false,
    },
    showIndicator: {
      type: Boolean,
      default: true,
    },
    autoplayInterval: {
      type: Number,
      default: 5000,
      validator(value) {
        return value >= 800;
      },
    },
    thresholdLimit: {
      type: Number,
      default: 0.5,
      validator(value) {
        return value >= 0 && value <= 1;
      },
    },
    wrapClass: {
      type: String,
      default: '',
    },
    showSkeleton: {
      type: Boolean,
      default: false,
    },
    skeletonOptions: {
      type: Object,
      default: () => ({}),
    },
    initialIndex: {
      type: Number,
      default: 0,
    },
    showIndicatorOnSlideshow: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['slideshow-change', 'slideshow-change-click'],
  data() {
    return {
      id: generateRandomId(),
      isReady: false,
      numberOfPages: 1,
      itemsLength: 3,
      scrollerWidth: null,
      childWidth: 0,
      currentPage: 1,
      scrollerX: 0,
      timer: null,
      noTransition: false,
      sliding: false,
      isFocus: false,
      prevIndex: -1,
      showControl: '',
      buttonFocus: false,
      hoverState: false,
      currentIndicator: '',
      isFirstTransitionDisabled: false,
      isInterruptedByUser: false,
      csgChevronSmallLeft,
      csgChevronSmallRight,
      csgVideoControlPause,
      csgVideoControlPlay,
      allowSwipe: true,
      transitionImg: '',
    };
  },
  computed: {
    indicatorWidth() {
      // button-icon-width + button-icon-margin-right + (indicator-border * 2 + indicator-padding-right + indicator-margin-right)
      // hardcoded to specific value instead of using css export because we need the values on SSR before scoped css styles are available
      return `${32 + 8 + (1 * 2 + 2 + 8) * this.list.length}px`;
    },
    currentImg() {
      if (this.allowSwipe) {
        return `url(${this.transitionImg})`;
      }
      return '';
    },
  },
  watch: {
    list(newList, oldList) {
      if (newList !== oldList) {
        this.change(1);
      }
    },
    initialIndex(newVal) {
      this.change(newVal + 1);
    },
  },
  mounted() {
    if (this.list.length) {
      this.eventHandler();
    }
    this.change(this.initialIndex + 1);
  },
  beforeUpdate() {
    if (this.numberOfPages !== this.list.length) {
      this.init();
    }
  },
  methods: {
    touchOutside(event) {
      const target = event?.target?.className;
      if (target && target !== 'play-control' && this.autoplayMode === 'auto') {
        this.startAutoplay();
      } else if (this.autoplayMode === 'manual' && this.backToFirst) {
        this.change(1);
      }
    },
    touchInside() {
      if (this.autoplayMode === 'auto') {
        this.stopAutoplay();
      }
    },
    onFocus() {
      this.isFocus = true;
      if (this.autoplayMode === 'auto') {
        this.buttonFocus = true;
        this.onEnter();
      }
    },
    onBlur() {
      this.isFocus = false;
      if (this.autoplayMode === 'auto') {
        this.buttonFocus = false;
        this.onLeave();
      }
    },
    scrollerStyle() {
      if (this.scrollerWidth != null) {
        let transitionDuration = 300;
        if (!this.isFirstTransitionDisabled || this.noTransition) {
          transitionDuration = 0;
        }

        return `
          width: ${this.scrollerWidth}px;
          transform: translateX(${this.scrollerX}px);
          transition-duration: ${transitionDuration}ms;
        `;
      }
      // move to the active slide
      return `transform: translateX(-${100 * (this.initialIndex + 1)}%);`;
    },
    scrollerTouchStart(event) {
      this.startX = this.scrollerX;
      this.touchstartX = event.changedTouches[0].screenX;
    },
    scrollerTouchMove(event) {
      event.preventDefault();
      this.touchmoveX = event.changedTouches[0].screenX;
      const distance = this.touchstartX - this.touchmoveX;
      this.scrollerX = this.startX - distance;
      this.prevIndex = this.currentPage + (distance > 0 ? 1 : -1);
    },
    scrollerTouchEnd(event) {
      this.touchendX = event.changedTouches[0].screenX;
      this.handleSwipe();
    },
    handleSwipe() {
      const threshold = 1 - this.thresholdLimit;
      const distance = Math.abs(this.touchstartX - this.touchendX);
      const hasPassedThreshold = distance > this.childWidth * threshold;
      if (hasPassedThreshold) {
        if (this.touchendX < this.touchstartX) {
          // swiped left
          if (!this.move(1)) {
            this.scrollerX = this.startX;
          }
        } else if (this.touchendX > this.touchstartX) {
          // swiped right
          if (!this.move(-1)) {
            this.scrollerX = this.startX;
          }
        }
      } else {
        this.scrollerX = this.startX;
        this.prevIndex = -1;
      }
    },
    checkImgLoaded(index) {
      const nextItem = this.slotsItems()[index];
      if (!nextItem) {
        return true;
      }
      const imgs = nextItem.getElementsByTagName('img');
      let completed = true;
      if (imgs && imgs.length) {
        [...imgs].forEach((img) => {
          img.addEventListener('load', () => {
            if (!img.complete) completed = false;
          });
        });
      }
      return completed;
    },
    /**
     * flag = 1 : next
     * flag = -1 : prev
     */
    move(flag) {
      if (!this.checkImgLoaded(this.currentPage + flag)) return false;
      if (this.sliding) return false;
      if (!this.isFirstTransitionDisabled) {
        this.isFirstTransitionDisabled = true;
      }
      this.sliding = true;
      const direction = flag < 0 ? 'left' : 'right';
      if (
        (flag < 0 && this.currentPage > 1)
        || (flag > 0 && this.currentPage < this.numberOfPages)
      ) {
        this.prevIndex = this.currentPage;
        this.currentPage += flag;
        this.scrollerX = this.scrollerPosition();
        this.$emit('slideshow-change', this.currentPage, direction);
        setTimeout(() => {
          this.sliding = false;
          this.prevIndex = -1;
        }, 300);
      } else {
        this.prevIndex = this.currentPage + flag;
        this.scrollerX = this.scrollerPosition(this.currentPage + flag);
        const newPage = flag > 0 ? 1 : this.numberOfPages;
        this.$emit('slideshow-change', newPage, direction);
        setTimeout(() => {
          this.currentPage = newPage;
          this.noTransition = true;
          this.scrollerX = this.scrollerPosition();
          this.prevIndex = -1;
          setTimeout(() => {
            this.noTransition = false;
            this.sliding = false;
          }, 300);
        }, 300);
      }
      return true;
    },
    change(index) {
      if (!this.checkImgLoaded(index)) return;
      if (this.isFirstTransitionDisabled) {
        this.isFirstTransitionDisabled = false;
      }
      this.currentIndicator = index - 1;
      this.currentPage = index;
      this.scrollerX = this.scrollerPosition();
      const direction = this.currentPage > this.prevIndex ? 'right' : 'left';
      this.prevIndex = this.currentPage;
      this.$emit('slideshow-change', this.currentPage, direction);
      setTimeout(() => {
        this.currentIndicator = null;
      }, 100);
    },
    numberSuffix(number) {
      const suffixes = {
        one: 'st',
        two: 'nd',
        few: 'rd',
        other: 'th',
      };
      return suffixes[
        new Intl.PluralRules('en', { type: 'ordinal' }).select(number)
      ];
    },
    scrollerPosition(activePage) {
      return -(
        this.childWidth
        * (activePage !== undefined ? activePage : this.currentPage)
      );
    },
    slotsItems() {
      if (this.$refs['v-scroller']) {
        return [
          ...this.$refs['v-scroller'].getElementsByClassName('slideshow-item'),
        ];
      }
      return [];
    },
    reset() {
      this.scrollerWidth = null;
      this.slotsItems().forEach((item) => {
        item.style.width = null;
      });
    },
    doSizing() {
      this.stopAutoplay();
      this.numberOfPages = this.list.length;
      this.itemsLength = this.numberOfPages + 2;
      this.childWidth = this.$refs[`v-slideshow-${this.id}`]?.getBoundingClientRect().width;
      this.slotsItems().forEach((item) => {
        item.style.width = `${this.childWidth}px`;
      });
      this.scrollerWidth = this.itemsLength * this.childWidth;
      this.scrollerX = this.scrollerPosition();
      this.isReady = true;
      if (this.autoplayMode === 'auto') {
        this.startAutoplay();
      }
    },
    init() {
      this.reset();
      this.$nextTick(() => {
        this.doSizing();
      });
    },
    eventHandler() {
      this.ro = new ResizeObserver(useDebounceFn(() => this.doSizing(), 300));
      this.ro.observe(this.$refs[`v-slideshow-${this.id}`]);
    },
    scrollMouseenter() {
      this.hoverState = true;
      this.onEnter();
    },
    getCurrentImgSrc(index) {
      let block = this.slotsItems()[index];
      let img = block?.querySelector('img');
      if (img && this.autoplayMode === 'hover-2-slides') {
        this.transitionImg = img.currentSrc;
      }
    },
    onEnter() {
      this.getCurrentImgSrc(2);
      if (this.buttonFocus || this.hoverState) {
        if (this.autoplayMode === 'auto') this.stopAutoplay();
        else if (this.autoplayMode === 'hover-2-slides') {
          clearInterval(this.timer);
          this.timer = setInterval(() => {
            if (this.currentPage <= 1 && this.allowSwipe) {
              this.change(2);
              this.allowSwipe = false;
            }
          }, 250);
        }
      }
    },
    scrollMouseleave(event) {
      const target = event?.relatedTarget?.className;
      if (!target || (target && target !== 'play-control')) {
        this.hoverState = false;
        this.getCurrentImgSrc(this.currentPage);
        this.onLeave();
      }
    },
    onLeave() {
      if (!this.buttonFocus && !this.hoverState) {
        this.isFocus = false;
        if (this.autoplayMode === 'auto') this.startAutoplay();
        else if (this.autoplayMode === 'hover-2-slides') {
          this.stopAutoplay();
          if (this.backToFirst) {
            if (this.autoplayMode === 'hover-2-slides') {
              this.allowSwipe = true;
              setTimeout(() => {
                this.change(1);
              }, 100);
            } else {
              this.change(1);
            }
          }
        } else if ((this.autoplayMode === 'manual') && this.backToFirst) {
          this.change(1);
        }
      }
    },
    startAutoplay() {
      this.showControl = 'pause';
      clearInterval(this.timer);
      this.timer = setInterval(() => {
        this.move(1);
      }, this.autoplayInterval);
    },
    stopAutoplay() {
      this.showControl = 'play';
      clearInterval(this.timer);
      this.timer = null;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@core/csg/src/scss/components/button-icon-mixin.scss";
@include csg-button-icon;

$transition: transform 300ms cubic-bezier(0.1, 0.57, 0.1, 1);

.v-slideshow-content {
  position: relative;
  overflow: hidden;
}

.v-button-wrapper {
  position: absolute;
  z-index: 2;
  width: rem-calc(32);
  height: rem-calc(32);
  top: 0;
  bottom: 0;
  margin: auto;
  background-color: transparent;
  transform: scale(0.5);
  transition: opacity 300ms, transform 600ms;
  opacity: 0;
  &.prev {
    left: rem-calc(8);
  }
  &.next {
    right: rem-calc(8);
  }
}

.v-slideshow-button {
  border-radius: 50%;
  border: 0;
  padding: 0;
  width: 100%;
  cursor: pointer;
  height: 100%;
  min-height: 0;
  mix-blend-mode: normal;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: background-color 100ms;

  &:hover {
    background-color: $hover-color;
  }

  .icon {
    display: block;
    margin: 0 auto;
  }
}

.slideshow-item {
  visibility: hidden;
  list-style-type: none;
  &.active {
    visibility: visible;
  }

  &:nth-child(2)::before {
    content: '';
    background: v-bind(currentImg);
    background-size: cover;
    position: absolute;
    width: inherit;
    height: 100%;
    opacity: 0;
    z-index: 1;
    transition: opacity 300ms linear;
  }
  .v-slideshow:hover &:nth-child(2)::before {
    opacity: 1;
    z-index: 1;
    transition: opacity 300ms linear;
  }
}

@media screen and (min-width: 769px) {
  .v-slideshow:hover .v-button-wrapper,
  .v-slideshow.is-focus .v-button-wrapper {
    opacity: 1;
    transform: scale(1);
  }
}

.v-slideshow-wrapper {
  overflow: hidden;
  position: relative;
  .v-scroller {
    -webkit-transition: $transition;
    -moz-transition: $transition;
    -ms-transition: $transition;
    -o-transition: $transition;
    transition: $transition;
  }
}

.v-dot-indicator-wrapper {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: rem-calc(8) 0;
  .button-control {
    margin-right: rem-calc(8);
  }
  li {
    display: inline-block;
    border-radius: 50%;
    opacity: 1;
    padding: rem-calc(2);
    margin-right: rem-calc(8);
    cursor: pointer;
    background-color: $gray-2-color;
    border: rem-calc(1) solid $gray-2-color;
    &.active {
      background-color: $black;
      border: rem-calc(1) solid $black;
    }
  }

  &.slideshow-indicator {
    position: absolute;
    bottom: 0;
    padding: rem-calc(4) 0;
    margin: 0px;
    background-color: $white-overlay-color-2;
    opacity: 0;
    transition: opacity 300ms;
  }
}

 .v-slideshow:hover .slideshow-indicator,
  .v-slideshow.is-focus .slideshow-indicator {
    opacity: 0.8;
  }

  @include breakpoint(medium down) {
    .v-dot-indicator-wrapper.slideshow-indicator  {
      display: none
    }
  }

.v-slideshow-button {
  background-color: rgba($white, 0.85);
}
</style>
<style lang="scss" brand="bcom" scoped>
.v-dot-indicator-wrapper {
  &.slideshow-indicator {
    display: none;
  }
}
</style>
